<template>
  <section v-if="inline" class="qqm qqm--inline">
    <header class="qqm__header qqm__header--inline modal-header">
      <div class="qqm__header-copy">
        <h3>Questions</h3>
        <p class="qqm__description">
          {{ totalCount }} question{{ totalCount !== 1 ? 's' : '' }} across {{ draft.length }} round{{ draft.length !== 1 ? 's' : '' }}.
          Add up to 10 questions per stage.
        </p>
      </div>
      <div class="qqm__header-actions">
        <span class="qqm__step-actions-label">Save actions</span>
        <button type="button" class="qqm__action qqm__action--primary qqm__action--inline-save" @click="handleSave">
          Save
        </button>
        <span class="qqm__step-status" :class="{
          'qqm__step-status--error': Boolean(saveError),
          'qqm__step-status--saved': Boolean(saveSuccess) && !saveError,
        }">
          {{ saveError ? 'Needs attention' : (saveSuccess ? 'Saved' : 'Unsaved') }}
        </span>
      </div>
    </header>

    <div class="qqm__tabs">
      <div class="qqm__round-strip">
        <div class="qqm__round-track">
          <div class="qqm__round-list" :style="roundListStyle" role="tablist">
            <button
              v-for="(round, rIdx) in draft"
              :key="round.id"
              type="button"
              role="tab"
              :aria-selected="activeRound === rIdx"
              class="qqm__round-pill"
              :class="{ 'qqm__round-pill--active': activeRound === rIdx }"
              @click="setActiveRound(rIdx)"
            >
              <span class="qqm__round-pill-label">{{ round.label }}</span>
              <span class="qqm__round-pill-qcount">{{ getRoundQuestionCount(round) }}</span>
              <button
                v-if="draft.length > 1"
                type="button"
                class="qqm__round-pill-remove"
                :aria-label="`Remove ${round.label}`"
                @click.stop="removeRound(rIdx)"
              >✕</button>
            </button>
          </div>
        </div>
        <div class="qqm__round-actions">
          <button
            type="button"
            class="qqm__round-pill qqm__round-pill--add"
            style="height: 32px; min-height: 32px; line-height: 32px;"
            @click="openAddRoundModal"
          >
            + Round
          </button>
        </div>
      </div>

      <div v-if="activeRoundData" class="qqm__round-settings">
        <label class="qqm__setting-field">
          <span class="qqm__setting-label">Label</span>
          <input
            type="text"
            class="qqm__setting-input"
            :value="activeRoundData.label"
            maxlength="40"
            @input="activeRoundData.label = ($event.target as HTMLInputElement).value"
          />
        </label>
        <label v-if="experienceMode === 'all-in-one' && activeStage" class="qqm__setting-field">
          <span class="qqm__setting-label">Stage mode</span>
          <select
            class="qqm__setting-input"
            :value="activeStage.type || 'quiz'"
            @change="setStageType(($event.target as HTMLSelectElement).value as 'quiz' | 'survey')"
          >
            <option value="quiz">Quiz</option>
            <option value="survey">Survey</option>
          </select>
        </label>
      </div>

      <div v-if="activeRoundData" class="qqm__stage-strip">
        <div class="qqm__stage-row">
          <div class="qqm__stage-track">
            <div class="qqm__stage-tabs">
              <div
                v-for="(stage, sIdx) in activeRoundData.stages"
                :key="stage.id"
                class="qqm__stage-tab"
                :class="{ 'qqm__stage-tab--active': activeStageIndex === sIdx }"
              >
                <button type="button" class="qqm__stage-tab-main" @click="setActiveStage(sIdx)">
                  <span>Stage {{ sIdx + 1 }}</span>
                  <span class="qqm__stage-count">{{ stage.questions.length }}</span>
                </button>
                <button
                  v-if="activeRoundData.stages.length > 1"
                  type="button"
                  class="qqm__stage-remove"
                  :aria-label="`Remove stage ${sIdx + 1}`"
                  @click.stop="removeStage(sIdx)"
                >✕</button>
              </div>
            </div>
          </div>
          <div class="qqm__stage-actions">
            <button type="button" class="qqm__stage-tab qqm__stage-tab--add" @click="addStage">
              + Stage
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="qqm__toolbar">
      <span class="qqm__count">
        {{ activeQuestions.length }} / {{ MAX_QUESTIONS }} questions
      </span>
      <button
        type="button"
        class="qqm__add-btn"
        :disabled="activeQuestions.length >= MAX_QUESTIONS"
        @click="addQuestion"
      >
        + Add Question
      </button>
    </div>

    <p v-if="saveError" class="qqm__save-error">
      {{ saveError }}
    </p>
    <p v-else-if="saveSuccess" class="qqm__save-success">
      {{ saveSuccess }}
    </p>

    <div ref="scrollBody" class="qqm__scroll-body qqm__scroll-body--inline modal-body">
      <div v-if="!activeRoundData || activeQuestions.length === 0" class="qqm__empty">
        No questions in this round yet — tap <strong>+ Add Question</strong> above.
      </div>

      <div
        v-for="(question, qIdx) in activeQuestions"
        :key="question.id"
        class="qqm__card"
        :data-question-index="qIdx"
      >
        <div class="qqm__card-header">
          <span class="qqm__q-number">Q{{ qIdx + 1 }}</span>
          <button
            type="button"
            class="qqm__remove-btn"
            aria-label="Remove question"
            @click="removeQuestion(qIdx)"
          >
            <template v-if="inline">Remove</template>
            <template v-else>✕</template>
          </button>
        </div>

        <label class="qqm__field qqm__field--wide">
          <span class="qqm__field-label">{{ getPromptTextLabel(question) }}</span>
          <input
            type="text"
            :placeholder="getPromptTextPlaceholder(question)"
            :value="question.text"
            maxlength="200"
            @input="question.text = ($event.target as HTMLInputElement).value"
          />
        </label>

        <details
          v-if="inline"
          class="qqm__advanced"
          :open="getPromptFormat(question) !== 'text' || getQuestionType(question) !== 'single-select'"
        >
          <summary class="qqm__advanced-summary">Question options</summary>
          <div class="qqm__advanced-body">
            <div class="qqm__field-grid">
              <label class="qqm__field">
                <span class="qqm__field-label">Prompt format</span>
                <select
                  class="qqm__setting-input"
                  :value="getPromptFormat(question)"
                  @change="setPromptFormat(question, ($event.target as HTMLSelectElement).value as 'text' | 'image' | 'video')"
                >
                  <option
                    v-for="formatOption in promptFormatOptions"
                    :key="formatOption.id"
                    :value="formatOption.id"
                  >
                    {{ formatOption.label }}
                  </option>
                </select>
              </label>
              <label class="qqm__field">
                <span class="qqm__field-label">Response type</span>
                <select
                  class="qqm__setting-input"
                  :value="getQuestionType(question)"
                  @change="setQuestionType(question, ($event.target as HTMLSelectElement).value as any)"
                >
                  <option
                    v-for="typeOption in getQuestionTypeOptions(activeStage?.type)"
                    :key="typeOption.id"
                    :value="typeOption.id"
                  >
                    {{ typeOption.label }}
                  </option>
                </select>
              </label>
            </div>

            <div v-if="getPromptFormat(question) === 'image'" class="qqm__field-grid">
              <label class="qqm__field">
                <span class="qqm__field-label">Image asset</span>
                <input
                  type="text"
                  class="qqm__setting-input"
                  :value="question.prompt?.imageAssetId || ''"
                  maxlength="200"
                  placeholder="Image asset id"
                  @input="setPromptField(question, 'imageAssetId', ($event.target as HTMLInputElement).value)"
                />
              </label>
              <label class="qqm__field">
                <span class="qqm__field-label">Image caption</span>
                <input
                  type="text"
                  class="qqm__setting-input"
                  :value="question.prompt?.text || ''"
                  maxlength="200"
                  placeholder="Optional caption or instruction"
                  @input="setPromptField(question, 'text', ($event.target as HTMLInputElement).value)"
                />
              </label>
            </div>

            <div v-if="getPromptFormat(question) === 'video'" class="qqm__field-grid">
              <label class="qqm__field">
                <span class="qqm__field-label">Video asset</span>
                <input
                  type="text"
                  class="qqm__setting-input"
                  :value="question.prompt?.videoAssetId || ''"
                  maxlength="200"
                  placeholder="Video asset id"
                  @input="setPromptField(question, 'videoAssetId', ($event.target as HTMLInputElement).value)"
                />
              </label>
              <label class="qqm__field">
                <span class="qqm__field-label">Video caption</span>
                <input
                  type="text"
                  class="qqm__setting-input"
                  :value="question.prompt?.text || ''"
                  maxlength="200"
                  placeholder="Optional caption or instruction"
                  @input="setPromptField(question, 'text', ($event.target as HTMLInputElement).value)"
                />
              </label>
            </div>
          </div>
        </details>

        <template v-else>
          <div class="qqm__field-grid">
            <label class="qqm__field">
              <span class="qqm__field-label">Prompt format</span>
              <select
                class="qqm__setting-input"
                :value="getPromptFormat(question)"
                @change="setPromptFormat(question, ($event.target as HTMLSelectElement).value as 'text' | 'image' | 'video')"
              >
                <option
                  v-for="formatOption in promptFormatOptions"
                  :key="formatOption.id"
                  :value="formatOption.id"
                >
                  {{ formatOption.label }}
                </option>
              </select>
            </label>
            <label class="qqm__field">
              <span class="qqm__field-label">Response type</span>
              <select
                class="qqm__setting-input"
                :value="getQuestionType(question)"
                @change="setQuestionType(question, ($event.target as HTMLSelectElement).value as any)"
              >
                <option
                  v-for="typeOption in getQuestionTypeOptions(activeStage?.type)"
                  :key="typeOption.id"
                  :value="typeOption.id"
                >
                  {{ typeOption.label }}
                </option>
              </select>
            </label>
          </div>

          <div v-if="getPromptFormat(question) === 'image'" class="qqm__field-grid">
            <label class="qqm__field">
              <span class="qqm__field-label">Image asset</span>
              <input
                type="text"
                class="qqm__setting-input"
                :value="question.prompt?.imageAssetId || ''"
                maxlength="200"
                placeholder="Image asset id"
                @input="setPromptField(question, 'imageAssetId', ($event.target as HTMLInputElement).value)"
              />
            </label>
            <label class="qqm__field">
              <span class="qqm__field-label">Image caption</span>
              <input
                type="text"
                class="qqm__setting-input"
                :value="question.prompt?.text || ''"
                maxlength="200"
                placeholder="Optional caption or instruction"
                @input="setPromptField(question, 'text', ($event.target as HTMLInputElement).value)"
              />
            </label>
          </div>

          <div v-if="getPromptFormat(question) === 'video'" class="qqm__field-grid">
            <label class="qqm__field">
              <span class="qqm__field-label">Video asset</span>
              <input
                type="text"
                class="qqm__setting-input"
                :value="question.prompt?.videoAssetId || ''"
                maxlength="200"
                placeholder="Video asset id"
                @input="setPromptField(question, 'videoAssetId', ($event.target as HTMLInputElement).value)"
              />
            </label>
            <label class="qqm__field">
              <span class="qqm__field-label">Video caption</span>
              <input
                type="text"
                class="qqm__setting-input"
                :value="question.prompt?.text || ''"
                maxlength="200"
                placeholder="Optional caption or instruction"
                @input="setPromptField(question, 'text', ($event.target as HTMLInputElement).value)"
              />
            </label>
          </div>
        </template>

        <div v-if="usesChoices(question)" class="qqm__choices">
          <div
            v-for="(choice, cIdx) in question.choices"
            :key="choice.id"
            class="qqm__choice-row"
            :class="{ 'qqm__choice-row--correct': isChoiceMarkedCorrect(question, choice.id) }"
          >
            <button
              type="button"
              class="qqm__badge"
              :class="{ 'qqm__badge--correct': isChoiceMarkedCorrect(question, choice.id) }"
              :aria-label="`Mark choice ${choice.id.toUpperCase()} as correct`"
              @click="toggleCorrectChoice(question, choice.id)"
            >
              {{ choice.id.toUpperCase() }}
            </button>
            <input
              type="text"
              class="qqm__choice-input"
              :placeholder="`Choice ${choice.id.toUpperCase()}...`"
              :value="choice.label"
              maxlength="150"
              @input="choice.label = ($event.target as HTMLInputElement).value"
            />
            <input
              v-if="getPromptFormat(question) !== 'text'"
              type="text"
              class="qqm__choice-input"
              :placeholder="`Image asset for ${choice.id.toUpperCase()}...`"
              :value="choice.imageAssetId || ''"
              maxlength="200"
              @input="choice.imageAssetId = ($event.target as HTMLInputElement).value"
            />
            <button
              v-if="question.choices.length > MIN_CHOICES"
              type="button"
              class="qqm__remove-btn qqm__remove-btn--small"
              aria-label="Remove choice"
              @click="removeChoice(qIdx, cIdx)"
            >
              ✕
            </button>
          </div>

          <button
            v-if="question.choices.length < MAX_CHOICES"
            type="button"
            class="qqm__add-choice-btn"
            @click="addChoice(qIdx)"
          >
            + Add Choice
          </button>
        </div>

        <div v-if="getQuestionType(question) === 'rating'" class="qqm__field-grid">
          <label class="qqm__field">
            <span class="qqm__field-label">Rating min</span>
            <input
              type="number"
              class="qqm__setting-input"
              :value="question.ratingScale?.min ?? 1"
              min="1"
              max="10"
              @input="setRatingScale(question, 'min', Number(($event.target as HTMLInputElement).value || 1))"
            />
          </label>
          <label class="qqm__field">
            <span class="qqm__field-label">Rating max</span>
            <input
              type="number"
              class="qqm__setting-input"
              :value="question.ratingScale?.max ?? 5"
              min="1"
              max="10"
              @input="setRatingScale(question, 'max', Number(($event.target as HTMLInputElement).value || 5))"
            />
          </label>
        </div>

        <div v-if="getQuestionType(question) === 'boolean'" class="qqm__field-grid">
          <label class="qqm__field">
            <span class="qqm__field-label">True label</span>
            <input
              type="text"
              class="qqm__setting-input"
              :value="question.booleanLabels?.true || ''"
              placeholder="Yes"
              @input="setBooleanLabel(question, 'true', ($event.target as HTMLInputElement).value)"
            />
          </label>
          <label class="qqm__field">
            <span class="qqm__field-label">False label</span>
            <input
              type="text"
              class="qqm__setting-input"
              :value="question.booleanLabels?.false || ''"
              placeholder="No"
              @input="setBooleanLabel(question, 'false', ($event.target as HTMLInputElement).value)"
            />
          </label>
        </div>

        <div v-if="getQuestionType(question) === 'free-text'" class="qqm__field-grid">
          <label class="qqm__field">
            <span class="qqm__field-label">Placeholder</span>
            <input
              type="text"
              class="qqm__setting-input"
              :value="question.validation?.placeholder || ''"
              @input="setValidationField(question, 'placeholder', ($event.target as HTMLInputElement).value)"
            />
          </label>
          <label class="qqm__field">
            <span class="qqm__field-label">Max length</span>
            <input
              type="number"
              class="qqm__setting-input"
              :value="question.validation?.maxLength ?? ''"
              min="1"
              @input="setValidationField(question, 'maxLength', Number(($event.target as HTMLInputElement).value || 0) || undefined)"
            />
          </label>
        </div>

        <p v-if="showCorrectAnswerHint(question)" class="qqm__hint">
          {{ getCorrectAnswerHint(question) }}
        </p>
      </div>
    </div>

  </section>

  <teleport v-else to="body">
    <div
      v-if="show"
      class="qqm-overlay asset-action-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Experience Questions"
      @click.self="emit('close')"
    >
      <div class="qqm asset-action-modal">
        <header class="qqm__header modal-header">
          <div class="qqm__header-copy">
            <h3>Experience Questions</h3>
            <p class="qqm__description">
              {{ totalCount }} question{{ totalCount !== 1 ? 's' : '' }} across {{ draft.length }} round{{ draft.length !== 1 ? 's' : '' }}.
              Add up to 10 questions per stage.
            </p>
          </div>
          <button
            type="button"
            class="qqm__close modal-close-button"
            aria-label="Close questions editor"
            @click="emit('close')"
          >
            &times;
          </button>
        </header>

        <!-- Round tabs -->
        <div class="qqm__tabs">
          <!-- Desktop: pills -->
          <div class="qqm__tab-list qqm__tab-list--pills" role="tablist">
            <button
              v-for="(round, rIdx) in draft"
              :key="round.id"
              type="button"
              role="tab"
              :aria-selected="activeRound === rIdx"
              class="qqm__tab"
              :class="{ 'qqm__tab--active': activeRound === rIdx }"
              @click="setActiveRound(rIdx)"
            >
              <span class="qqm__tab-label">{{ round.label }}</span>
              <span class="qqm__tab-qcount">{{ getRoundQuestionCount(round) }}</span>
              <button
                v-if="draft.length > 1"
                type="button"
                class="qqm__tab-remove"
                :aria-label="`Remove ${round.label}`"
                @click.stop="removeRound(rIdx)"
              >✕</button>
            </button>
            <button
              type="button"
              class="qqm__tab qqm__tab--add"
              @click="openAddRoundModal"
            >
              + Round
            </button>
          </div>
          <!-- Mobile: dropdown -->
          <div class="qqm__tab-list qqm__tab-list--select">
            <select
              class="qqm__round-select"
              :value="activeRound"
              @change="setActiveRound(Number(($event.target as HTMLSelectElement).value))"
            >
              <option
                v-for="(round, rIdx) in draft"
                :key="round.id"
                :value="rIdx"
              >{{ round.label }} ({{ getRoundQuestionCount(round) }} questions)</option>
            </select>
            <button
              type="button"
              class="qqm__round-add-btn"
              @click="openAddRoundModal"
            >
              + Round
            </button>
          </div>

          <!-- Round settings bar — only shown for all-in-one mode -->
          <div v-if="activeRoundData && experienceMode === 'all-in-one' && activeStage" class="qqm__round-settings">
            <label class="qqm__setting-field">
              <span class="qqm__setting-label">Stage mode</span>
              <select
                class="qqm__setting-input"
                :value="activeStage.type || 'quiz'"
                @change="setStageType(($event.target as HTMLSelectElement).value as 'quiz' | 'survey')"
              >
                <option value="quiz">Quiz</option>
                <option value="survey">Survey</option>
              </select>
            </label>
          </div>

          <div v-if="activeRoundData && (activeRoundData.stages.length > 1 || experienceMode === 'all-in-one')" class="qqm__stage-strip">
            <div class="qqm__stage-tabs">
              <div
                v-for="(stage, sIdx) in activeRoundData.stages"
                :key="stage.id"
                class="qqm__stage-item"
              >
                <button
                  type="button"
                  class="qqm__stage-tab qqm__stage-tab-main"
                  :class="{ 'qqm__stage-tab--active': activeStageIndex === sIdx }"
                  @click="setActiveStage(sIdx)"
                >
                  <span>Stage {{ sIdx + 1 }}</span>
                  <span class="qqm__stage-count">{{ stage.questions.length }}</span>
                </button>
                <button
                  v-if="activeRoundData.stages.length > 1"
                  type="button"
                  class="qqm__stage-remove"
                  :aria-label="`Remove stage ${sIdx + 1}`"
                  @click.stop="removeStage(sIdx)"
                >✕</button>
              </div>
              <button type="button" class="qqm__stage-tab qqm__stage-tab--add" @click="addStage">
                + Stage
              </button>
            </div>
          </div>
        </div>

        <!-- Per-round toolbar -->
        <div class="qqm__toolbar">
          <span class="qqm__count">
            {{ activeQuestions.length }} / {{ MAX_QUESTIONS }} questions
          </span>
          <button
            type="button"
            class="qqm__add-btn"
            :disabled="activeQuestions.length >= MAX_QUESTIONS"
            @click="addQuestion"
          >
            + Add Question
          </button>
        </div>

        <p v-if="saveError" class="qqm__save-error">
          {{ saveError }}
        </p>

        <!-- Question summary list (A7: capped to 4 visible rows, then scroll) -->
        <div ref="scrollBody" class="qqm__scroll-body qqm__scroll-body--questions modal-body">
          <div v-if="!activeRoundData || activeQuestions.length === 0" class="qqm__empty">
            No questions in this round yet — tap <strong>+ Add Question</strong> above.
          </div>

          <div
            v-for="(question, qIdx) in activeQuestions"
            :key="question.id"
            class="qqm__q-row qqm__q-row--with-remove"
          >
            <button
              type="button"
              class="qqm__q-row-main"
              :aria-label="`Edit question ${qIdx + 1}`"
              @click="editingQuestionIndex = qIdx"
            >
              <span class="qqm__q-row-number">Q{{ qIdx + 1 }}</span>
              <span class="qqm__q-row-text">{{ question.text || 'Untitled question' }}</span>
              <span class="qqm__q-row-meta">{{ question.choices.length }} choices</span>
              <span class="qqm__q-row-arrow">›</span>
            </button>
            <button
              type="button"
              class="qqm__q-row-remove"
              :aria-label="`Remove question ${qIdx + 1}`"
              @click.stop="removeQuestion(qIdx)"
            >
              ✕
            </button>
          </div>
        </div>

        <!-- Nested question editor modal -->
        <teleport to="body">
          <div
            v-if="editingQuestionIndex !== null && activeQuestions[editingQuestionIndex]"
            class="qqm-overlay qqm-overlay--nested"
            role="dialog"
            aria-modal="true"
            aria-label="Edit question"
            @click.self="editingQuestionIndex = null"
          >
            <div class="qqm qqm--nested">
              <header class="qqm__header modal-header">
                <div class="qqm__header-copy">
                  <h3>Q{{ editingQuestionIndex + 1 }}</h3>
                </div>
                <button
                  type="button"
                  class="qqm__close modal-close-button"
                  aria-label="Close question editor"
                  @click="editingQuestionIndex = null"
                >&times;</button>
              </header>

              <div class="qqm__scroll-body modal-body">
                <label class="qqm__field qqm__field--wide">
                  <span class="qqm__field-label">{{ getPromptTextLabel(activeQuestions[editingQuestionIndex]) }}</span>
                  <input
                    type="text"
                    :placeholder="getPromptTextPlaceholder(activeQuestions[editingQuestionIndex])"
                    :value="activeQuestions[editingQuestionIndex].text"
                    maxlength="200"
                    @input="activeQuestions[editingQuestionIndex].text = ($event.target as HTMLInputElement).value"
                  />
                </label>

                <div class="qqm__field-grid">
                  <label class="qqm__field">
                    <span class="qqm__field-label">Prompt format</span>
                    <select
                      class="qqm__setting-input"
                      :value="getPromptFormat(activeQuestions[editingQuestionIndex])"
                      @change="setPromptFormat(activeQuestions[editingQuestionIndex], ($event.target as HTMLSelectElement).value as 'text' | 'image' | 'video')"
                    >
                      <option v-for="f in promptFormatOptions" :key="f.id" :value="f.id">{{ f.label }}</option>
                    </select>
                  </label>
                  <label class="qqm__field">
                    <span class="qqm__field-label">Response type</span>
                    <select
                      class="qqm__setting-input"
                      :value="getQuestionType(activeQuestions[editingQuestionIndex])"
                      @change="setQuestionType(activeQuestions[editingQuestionIndex], ($event.target as HTMLSelectElement).value as any)"
                    >
                      <option v-for="t in getQuestionTypeOptions(activeStage?.type)" :key="t.id" :value="t.id">{{ t.label }}</option>
                    </select>
                  </label>
                </div>

                <div v-if="getPromptFormat(activeQuestions[editingQuestionIndex]) === 'image'" class="qqm__field-grid">
                  <label class="qqm__field">
                    <span class="qqm__field-label">Image asset</span>
                    <input type="text" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].prompt?.imageAssetId || ''" maxlength="200" placeholder="Image asset id" @input="setPromptField(activeQuestions[editingQuestionIndex], 'imageAssetId', ($event.target as HTMLInputElement).value)" />
                  </label>
                  <label class="qqm__field">
                    <span class="qqm__field-label">Image caption</span>
                    <input type="text" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].prompt?.text || ''" maxlength="200" placeholder="Optional caption" @input="setPromptField(activeQuestions[editingQuestionIndex], 'text', ($event.target as HTMLInputElement).value)" />
                  </label>
                </div>

                <div v-if="getPromptFormat(activeQuestions[editingQuestionIndex]) === 'video'" class="qqm__field-grid">
                  <label class="qqm__field">
                    <span class="qqm__field-label">Video asset</span>
                    <input type="text" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].prompt?.videoAssetId || ''" maxlength="200" placeholder="Video asset id" @input="setPromptField(activeQuestions[editingQuestionIndex], 'videoAssetId', ($event.target as HTMLInputElement).value)" />
                  </label>
                  <label class="qqm__field">
                    <span class="qqm__field-label">Video caption</span>
                    <input type="text" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].prompt?.text || ''" maxlength="200" placeholder="Optional caption" @input="setPromptField(activeQuestions[editingQuestionIndex], 'text', ($event.target as HTMLInputElement).value)" />
                  </label>
                </div>

                <div v-if="usesChoices(activeQuestions[editingQuestionIndex])" class="qqm__choices">
                  <div
                    v-for="(choice, cIdx) in activeQuestions[editingQuestionIndex].choices"
                    :key="choice.id"
                    class="qqm__choice-row qqm__choice-row--draggable"
                    :class="{ 'qqm__choice-row--correct': isChoiceMarkedCorrect(activeQuestions[editingQuestionIndex], choice.id), 'qqm__choice-row--dragging': dragChoiceContext && dragChoiceContext.qIdx === editingQuestionIndex && dragChoiceContext.fromIdx === cIdx }"
                    :draggable="activeQuestions[editingQuestionIndex].choices.length > 1"
                    @dragstart="onChoiceDragStart(editingQuestionIndex!, cIdx, $event)"
                    @dragover="onChoiceDragOver(editingQuestionIndex!, cIdx, $event)"
                    @drop="onChoiceDrop(editingQuestionIndex!, cIdx, $event)"
                    @dragend="onChoiceDragEnd"
                  >
                    <span class="qqm__choice-handle" aria-hidden="true">⋮⋮</span>
                    <button type="button" class="qqm__badge" :class="{ 'qqm__badge--correct': isChoiceMarkedCorrect(activeQuestions[editingQuestionIndex], choice.id) }" @click="toggleCorrectChoice(activeQuestions[editingQuestionIndex], choice.id)">{{ choice.id.toUpperCase() }}</button>
                    <input type="text" class="qqm__choice-input" :placeholder="`Choice ${choice.id.toUpperCase()}...`" :value="choice.label" maxlength="150" @input="choice.label = ($event.target as HTMLInputElement).value" />
                    <button v-if="activeQuestions[editingQuestionIndex].choices.length > MIN_CHOICES" type="button" class="qqm__remove-btn qqm__remove-btn--small" aria-label="Remove choice" @click="removeChoice(editingQuestionIndex, cIdx)">✕</button>
                  </div>
                  <button v-if="activeQuestions[editingQuestionIndex].choices.length < MAX_CHOICES" type="button" class="qqm__add-choice-btn" @click="addChoice(editingQuestionIndex)">+ Add Choice</button>
                </div>

                <div v-if="getQuestionType(activeQuestions[editingQuestionIndex]) === 'rating'" class="qqm__field-grid">
                  <label class="qqm__field"><span class="qqm__field-label">Rating min</span><input type="number" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].ratingScale?.min ?? 1" min="1" max="10" @input="setRatingScale(activeQuestions[editingQuestionIndex], 'min', Number(($event.target as HTMLInputElement).value || 1))" /></label>
                  <label class="qqm__field"><span class="qqm__field-label">Rating max</span><input type="number" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].ratingScale?.max ?? 5" min="1" max="10" @input="setRatingScale(activeQuestions[editingQuestionIndex], 'max', Number(($event.target as HTMLInputElement).value || 5))" /></label>
                </div>

                <div v-if="getQuestionType(activeQuestions[editingQuestionIndex]) === 'boolean'" class="qqm__field-grid">
                  <label class="qqm__field"><span class="qqm__field-label">True label</span><input type="text" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].booleanLabels?.true || ''" placeholder="Yes" @input="setBooleanLabel(activeQuestions[editingQuestionIndex], 'true', ($event.target as HTMLInputElement).value)" /></label>
                  <label class="qqm__field"><span class="qqm__field-label">False label</span><input type="text" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].booleanLabels?.false || ''" placeholder="No" @input="setBooleanLabel(activeQuestions[editingQuestionIndex], 'false', ($event.target as HTMLInputElement).value)" /></label>
                </div>

                <div v-if="getQuestionType(activeQuestions[editingQuestionIndex]) === 'free-text'" class="qqm__field-grid">
                  <label class="qqm__field"><span class="qqm__field-label">Placeholder</span><input type="text" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].validation?.placeholder || ''" @input="setValidationField(activeQuestions[editingQuestionIndex], 'placeholder', ($event.target as HTMLInputElement).value)" /></label>
                  <label class="qqm__field"><span class="qqm__field-label">Max length</span><input type="number" class="qqm__setting-input" :value="activeQuestions[editingQuestionIndex].validation?.maxLength ?? ''" min="1" @input="setValidationField(activeQuestions[editingQuestionIndex], 'maxLength', Number(($event.target as HTMLInputElement).value || 0) || undefined)" /></label>
                </div>

                <p v-if="showCorrectAnswerHint(activeQuestions[editingQuestionIndex])" class="qqm__hint">
                  {{ getCorrectAnswerHint(activeQuestions[editingQuestionIndex]) }}
                </p>
              </div>

              <footer class="qqm__footer modal-footer">
                <div class="qqm__actions">
                  <button type="button" class="qqm__action qqm__action--secondary" @click="removeQuestion(editingQuestionIndex!); editingQuestionIndex = null">Remove</button>
                  <button type="button" class="qqm__action qqm__action--primary" @click="editingQuestionIndex = null">Done</button>
                </div>
              </footer>
            </div>
          </div>
        </teleport>

        <footer class="qqm__footer modal-footer">
          <div class="qqm__actions">
            <button type="button" class="qqm__action qqm__action--secondary" @click="emit('close')">
              Cancel
            </button>
            <button type="button" class="qqm__action qqm__action--primary" @click="handleSave">
              Save
            </button>
          </div>
        </footer>
      </div>
    </div>
  </teleport>

  <!-- Add Round modal -->
  <teleport to="body">
    <div
      v-if="showAddRoundModal"
      class="qqm-overlay qqm-overlay--nested"
      role="dialog"
      aria-modal="true"
      aria-label="Add round"
      @click.self="showAddRoundModal = false"
    >
      <div class="qqm qqm--nested qqm--add-round">
        <header class="qqm__header modal-header">
          <div class="qqm__header-copy">
            <h3>Add Round</h3>
            <p class="qqm__description">Give this round a name.</p>
          </div>
          <button
            type="button"
            class="qqm__close modal-close-button"
            aria-label="Cancel"
            @click="showAddRoundModal = false"
          >&times;</button>
        </header>

        <div class="qqm__scroll-body modal-body" style="padding: 1rem 1.25rem;">
          <label class="qqm__field qqm__field--wide">
            <span class="qqm__field-label">Round name</span>
            <input
              type="text"
              :value="newRoundName"
              maxlength="40"
              placeholder="e.g. Week 1, Finals, Bonus Round"
              @input="newRoundName = ($event.target as HTMLInputElement).value"
              @keydown.enter="confirmAddRound"
            />
          </label>
        </div>

        <footer class="qqm__footer modal-footer">
          <div class="qqm__actions">
            <button type="button" class="qqm__action qqm__action--secondary" @click="showAddRoundModal = false">Cancel</button>
            <button type="button" class="qqm__action qqm__action--primary" @click="confirmAddRound">Add</button>
          </div>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch, nextTick } from 'vue'
import type { QuizQuestion, QuizRound } from '@/types/form.types'

const MAX_QUESTIONS = 10
const MIN_CHOICES = 2
const MAX_CHOICES = 4
const CHOICE_LETTERS = ['a', 'b', 'c', 'd'] as const

const props = defineProps<{
  show: boolean
  inline?: boolean
  rounds: QuizRound[]
  experienceMode: 'quiz' | 'survey' | 'all-in-one'
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', value: QuizRound[]): void
}>()

const draft = reactive<QuizRound[]>([])
const activeRound = ref(0)
const activeStageIndex = ref(0)
const editingQuestionIndex = ref<number | null>(null)
const scrollBody = ref<HTMLElement | null>(null)
const saveError = ref('')
const saveSuccess = ref('')

const activeRoundData = computed(() => draft[activeRound.value] ?? null)
const activeStage = computed(() => activeRoundData.value?.stages[activeStageIndex.value] ?? null)
const activeQuestions = computed(() => activeStage.value?.questions ?? [])

const totalCount = computed(() => draft.reduce((sum, round) => sum + round.stages.reduce((stageSum, stage) => stageSum + stage.questions.length, 0), 0))

const promptFormatOptions = [
  { id: 'text', label: 'Text' },
  { id: 'image', label: 'Image' },
  { id: 'video', label: 'Video' },
] as const

function normalizeLegacyQuizType(type: QuizQuestion['type'] | undefined, stageType?: 'quiz' | 'survey') {
  if (type === 'quiz') {
    return stageType === 'survey' ? 'single-select' : 'single-select'
  }
  return type || (stageType === 'survey' ? 'single-select' : 'single-select')
}

function deepCloneRounds(rounds: QuizRound[]): QuizRound[] {
  return rounds.map(r => ({
    ...r,
    stages: (r.stages ?? []).map(stage => ({
      ...stage,
      type: stage.type || 'quiz',
      questions: stage.questions.map(q => ({
        ...q,
        type: normalizeLegacyQuizType(q.type, stage.type || 'quiz'),
        promptFormat: q.promptFormat || ((q.prompt?.videoAssetId && 'video') || (q.prompt?.imageAssetId && 'image') || 'text'),
        prompt: q.prompt ? { ...q.prompt } : undefined,
        ratingScale: q.ratingScale ? { ...q.ratingScale } : undefined,
        booleanLabels: q.booleanLabels ? { ...q.booleanLabels } : undefined,
        validation: q.validation ? { ...q.validation } : undefined,
        choices: q.choices.map(c => ({ ...c })),
      })),
    })),
  }))
}

watch(() => [props.show, props.inline, props.rounds] as const, ([visible, inline]) => {
  if (!visible && !inline) return
  draft.splice(0, draft.length, ...deepCloneRounds(props.rounds))
  activeRound.value = 0
  activeStageIndex.value = 0
  saveError.value = ''
  if (!inline) {
    saveSuccess.value = ''
  }
}, { immediate: true, deep: true })

function generateId(): string {
  return Math.random().toString(36).slice(2, 9)
}

const roundListStyle = computed(() => ({
  gridTemplateColumns: `repeat(${Math.max(draft.length, 1)}, minmax(8.4rem, 1fr))`,
}))

function getNextDefaultRoundNumber(): number {
  const usedNumbers = new Set(
    draft
      .map((round) => round.label.match(/^Round (\d+)$/i)?.[1])
      .filter((value): value is string => Boolean(value))
      .map((value) => Number.parseInt(value, 10))
      .filter((value) => Number.isFinite(value) && value > 0),
  )

  let nextNumber = 1
  while (usedNumbers.has(nextNumber)) {
    nextNumber += 1
  }
  return nextNumber
}

const showAddRoundModal = ref(false)
const newRoundName = ref('')

function openAddRoundModal() {
  const nextRoundNumber = getNextDefaultRoundNumber()
  newRoundName.value = `Round ${nextRoundNumber}`
  showAddRoundModal.value = true
}

function confirmAddRound() {
  saveSuccess.value = ''
  const defaultName = `Round ${getNextDefaultRoundNumber()}`
  draft.push({
    id: generateId(),
    label: newRoundName.value.trim() || defaultName,
    stages: [
      {
        id: `${generateId()}-stage-1`,
        order: 1,
        type: props.experienceMode === 'survey' ? 'survey' : 'quiz',
        questions: [],
      },
    ],
  })
  activeRound.value = draft.length - 1
  activeStageIndex.value = 0
  showAddRoundModal.value = false
  syncInlineDraft()
}

function removeRound(rIdx: number) {
  saveSuccess.value = ''
  draft.splice(rIdx, 1)
  if (activeRound.value >= draft.length) {
    activeRound.value = draft.length - 1
  }
  activeStageIndex.value = 0
  syncInlineDraft()
}

function setActiveRound(rIdx: number) {
  saveSuccess.value = ''
  activeRound.value = rIdx
  activeStageIndex.value = 0
}

function setActiveStage(sIdx: number) {
  saveSuccess.value = ''
  activeStageIndex.value = sIdx
}

function addStage() {
  saveSuccess.value = ''
  const round = activeRoundData.value
  if (!round) return
  round.stages.push({
    id: `${round.id}-stage-${round.stages.length + 1}-${generateId()}`,
    order: round.stages.length + 1,
    type: props.experienceMode === 'survey' ? 'survey' : (props.experienceMode === 'all-in-one' ? (round.stages[round.stages.length - 1]?.type || 'quiz') : 'quiz'),
    questions: [],
  })
  activeStageIndex.value = round.stages.length - 1
  syncInlineDraft()
}

function removeStage(sIdx: number) {
  saveSuccess.value = ''
  const round = activeRoundData.value
  if (!round || round.stages.length <= 1) return
  round.stages.splice(sIdx, 1)
  round.stages.forEach((stage, index) => {
    stage.order = index + 1
  })
  if (activeStageIndex.value >= round.stages.length) {
    activeStageIndex.value = round.stages.length - 1
  }
  syncInlineDraft()
}

function addQuestion() {
  saveError.value = ''
  saveSuccess.value = ''
  const stage = activeStage.value
  if (!stage || stage.questions.length >= MAX_QUESTIONS) return
  const questionType = stage.type === 'survey' ? 'single-select' : 'single-select'
  stage.questions.push({
    id: generateId(),
    text: '',
    choices: [
      { id: 'a', label: '' },
      { id: 'b', label: '' },
    ],
    correctChoiceId: questionType === 'single-select' ? '' : undefined,
    correctChoiceIds: [],
    type: questionType,
    promptFormat: 'text',
  })
  const newQuestionIndex = stage.questions.length - 1
  nextTick(() => {
    const card = scrollBody.value?.querySelector<HTMLElement>(`[data-question-index="${newQuestionIndex}"]`)
    scrollCardIntoEditingView(card)
    const firstInput = card?.querySelector<HTMLInputElement>('input[type="text"], textarea')
    firstInput?.focus({ preventScroll: true })
  })
}

function removeQuestion(qIdx: number) {
  saveError.value = ''
  saveSuccess.value = ''
  activeStage.value?.questions.splice(qIdx, 1)
  syncInlineDraft()
}

function addChoice(qIdx: number) {
  saveSuccess.value = ''
  const question = activeStage.value?.questions[qIdx]
  if (!question || question.choices.length >= MAX_CHOICES) return
  const existing = new Set(question.choices.map(c => c.id))
  const nextId = CHOICE_LETTERS.find(l => !existing.has(l))
  if (!nextId) return
  question.choices.push({ id: nextId, label: '' })
}

function removeChoice(qIdx: number, cIdx: number) {
  saveSuccess.value = ''
  const question = activeStage.value?.questions[qIdx]
  if (!question) return
  const removed = question.choices.splice(cIdx, 1)[0]
  if (question.correctChoiceId === removed.id) {
    question.correctChoiceId = ''
  }
  if (question.correctChoiceIds?.includes(removed.id)) {
    question.correctChoiceIds = question.correctChoiceIds.filter((choiceId) => choiceId !== removed.id)
  }
}

// A6: Native HTML5 drag-and-drop reorder for question choices.
// Choice order is implicit from array position — no schema change required.
const dragChoiceContext = ref<{ qIdx: number; fromIdx: number } | null>(null)

function onChoiceDragStart(qIdx: number, cIdx: number, event: DragEvent) {
  dragChoiceContext.value = { qIdx, fromIdx: cIdx }
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    // Required by Firefox to actually start the drag.
    event.dataTransfer.setData('text/plain', String(cIdx))
  }
}

function onChoiceDragOver(qIdx: number, _cIdx: number, event: DragEvent) {
  const ctx = dragChoiceContext.value
  if (!ctx || ctx.qIdx !== qIdx) return
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
}

function onChoiceDrop(qIdx: number, toIdx: number, event: DragEvent) {
  event.preventDefault()
  const ctx = dragChoiceContext.value
  dragChoiceContext.value = null
  if (!ctx || ctx.qIdx !== qIdx) return
  const fromIdx = ctx.fromIdx
  if (fromIdx === toIdx) return
  const question = activeStage.value?.questions[qIdx]
  if (!question) return
  const next = question.choices.slice()
  const [moved] = next.splice(fromIdx, 1)
  next.splice(toIdx, 0, moved)
  question.choices = next
}

function onChoiceDragEnd() {
  dragChoiceContext.value = null
}

function setStageType(stageType: 'quiz' | 'survey') {
  saveSuccess.value = ''
  const stage = activeStage.value
  if (!stage) return
  stage.type = stageType
  stage.questions.forEach((question) => setQuestionType(question, stageType === 'survey' ? normalizeLegacyQuizType(question.type, 'survey') : normalizeLegacyQuizType(question.type, 'quiz')))
  syncInlineDraft()
}

function getQuestionTypeOptions(stageType?: 'quiz' | 'survey') {
  if (stageType === 'survey') {
    return [
      { id: 'single-select', label: 'Single Select' },
      { id: 'multi-select', label: 'Multi Select' },
      { id: 'free-text', label: 'Free Text' },
      { id: 'rating', label: 'Rating' },
      { id: 'boolean', label: 'Boolean' },
    ]
  }
  return [
    { id: 'single-select', label: 'Single choice' },
    { id: 'multi-select', label: 'Multiple choice' },
  ]
}

function getQuestionType(question: QuizQuestion) {
  return normalizeLegacyQuizType(question.type, activeStage.value?.type)
}

function getPromptFormat(question: QuizQuestion) {
  return question.promptFormat || ((question.prompt?.videoAssetId && 'video') || (question.prompt?.imageAssetId && 'image') || 'text')
}

function getPromptTextLabel(question: QuizQuestion) {
  const promptFormat = getPromptFormat(question)
  if (promptFormat === 'image') return 'Image question text'
  if (promptFormat === 'video') return 'Video question text'
  return activeStage.value?.type === 'survey' ? 'Survey question text' : 'Question text'
}

function getPromptTextPlaceholder(question: QuizQuestion) {
  const promptFormat = getPromptFormat(question)
  if (promptFormat === 'image') return 'Question or instruction shown with the image...'
  if (promptFormat === 'video') return 'Question or instruction shown with the video...'
  return 'Enter the question the user sees...'
}

function setPromptFormat(question: QuizQuestion, format: 'text' | 'image' | 'video') {
  saveSuccess.value = ''
  question.promptFormat = format
  question.prompt = question.prompt || {}
  if (format !== 'image') {
    question.prompt.imageAssetId = undefined
  }
  if (format !== 'video') {
    question.prompt.videoAssetId = undefined
  }
  if (format === 'text') {
    question.prompt.text = undefined
  }
}

function usesChoices(question: QuizQuestion) {
  const type = getQuestionType(question)
  return type === 'single-select' || type === 'multi-select'
}

function isChoiceMarkedCorrect(question: QuizQuestion, choiceId: string) {
  const type = getQuestionType(question)
  if (type === 'multi-select') {
    return Boolean(question.correctChoiceIds?.includes(choiceId))
  }
  return question.correctChoiceId === choiceId
}

function toggleCorrectChoice(question: QuizQuestion, choiceId: string) {
  saveSuccess.value = ''
  const type = getQuestionType(question)
  if (type === 'multi-select') {
    const existing = new Set(question.correctChoiceIds || [])
    if (existing.has(choiceId)) existing.delete(choiceId)
    else existing.add(choiceId)
    question.correctChoiceIds = Array.from(existing)
    return
  }
  question.correctChoiceId = question.correctChoiceId === choiceId ? '' : choiceId
}

function setQuestionType(question: QuizQuestion, type: QuizQuestion['type']) {
  saveSuccess.value = ''
  question.type = type
  if (type === 'free-text') {
    question.choices = []
    question.correctChoiceId = undefined
    question.correctChoiceIds = []
    return
  }
  if (type === 'rating') {
    question.choices = []
    question.correctChoiceId = undefined
    question.correctChoiceIds = []
    question.ratingScale = question.ratingScale || { min: 1, max: 5 }
    return
  }
  if (type === 'boolean') {
    question.choices = []
    question.correctChoiceId = undefined
    question.correctChoiceIds = []
    question.booleanLabels = question.booleanLabels || { true: 'Yes', false: 'No' }
    return
  }
  if (!question.choices.length) {
    question.choices = [
      { id: 'a', label: '' },
      { id: 'b', label: '' },
    ]
  }
  if (type === 'multi-select') {
    question.correctChoiceId = undefined
    question.correctChoiceIds = question.correctChoiceIds || []
  } else if (activeStage.value?.type !== 'survey') {
    question.correctChoiceId = question.correctChoiceId || ''
    question.correctChoiceIds = []
  } else {
    question.correctChoiceId = undefined
    question.correctChoiceIds = []
  }
}

function setPromptField(question: QuizQuestion, key: 'text' | 'imageAssetId' | 'videoAssetId', value: string) {
  saveSuccess.value = ''
  question.prompt = question.prompt || {}
  question.prompt[key] = value || undefined
}

function setRatingScale(question: QuizQuestion, key: 'min' | 'max', value: number) {
  saveSuccess.value = ''
  question.ratingScale = question.ratingScale || { min: 1, max: 5 }
  question.ratingScale[key] = value
}

function setBooleanLabel(question: QuizQuestion, key: 'true' | 'false', value: string) {
  saveSuccess.value = ''
  question.booleanLabels = question.booleanLabels || {}
  question.booleanLabels[key] = value || undefined
}

function setValidationField(question: QuizQuestion, key: 'placeholder' | 'maxLength', value: string | number | undefined) {
  saveSuccess.value = ''
  question.validation = question.validation || {}
  ;(question.validation as any)[key] = value || undefined
}

function showCorrectAnswerHint(question: QuizQuestion) {
  const type = getQuestionType(question)
  return activeStage.value?.type !== 'survey' && (type === 'single-select' || type === 'multi-select') && !isChoiceAnswerConfigured(question)
}

function isChoiceAnswerConfigured(question: QuizQuestion) {
  const type = getQuestionType(question)
  if (type === 'multi-select') {
    return Boolean(question.correctChoiceIds?.length)
  }
  return Boolean(question.correctChoiceId)
}

function getCorrectAnswerHint(question: QuizQuestion) {
  return getQuestionType(question) === 'multi-select'
    ? 'Tap the letter badges to mark all correct answers.'
    : 'Tap a letter badge to mark the correct answer.'
}

function findFirstInvalidQuestion(): { roundIndex: number, stageIndex: number, questionIndex: number, message: string } | null {
  for (let roundIndex = 0; roundIndex < draft.length; roundIndex += 1) {
    const round = draft[roundIndex]
    for (let stageIndex = 0; stageIndex < round.stages.length; stageIndex += 1) {
      const stage = round.stages[stageIndex]
      for (let questionIndex = 0; questionIndex < stage.questions.length; questionIndex += 1) {
        const question = stage.questions[questionIndex]
        const type = getQuestionType(question)
        const promptFormat = getPromptFormat(question)
        if (!question.text.trim()) {
          return { roundIndex, stageIndex, questionIndex, message: `Question ${questionIndex + 1} in ${round.label} needs question text.` }
        }
        if (promptFormat === 'image' && !question.prompt?.imageAssetId?.trim()) {
          return { roundIndex, stageIndex, questionIndex, message: `Question ${questionIndex + 1} in ${round.label} needs an image asset.` }
        }
        if (promptFormat === 'video' && !question.prompt?.videoAssetId?.trim()) {
          return { roundIndex, stageIndex, questionIndex, message: `Question ${questionIndex + 1} in ${round.label} needs a video asset.` }
        }
        if (type === 'free-text') continue
        if (type === 'rating') {
          if (!(question.ratingScale?.min ?? 1) || !(question.ratingScale?.max ?? 5)) {
            return { roundIndex, stageIndex, questionIndex, message: `Question ${questionIndex + 1} in ${round.label} needs a rating scale.` }
          }
          continue
        }
        if (type === 'boolean') continue
        const hasValidChoices = question.choices.length >= 2 && question.choices.every(c => c.label.trim() || c.imageAssetId?.trim())
        if (!hasValidChoices) {
          return { roundIndex, stageIndex, questionIndex, message: `Question ${questionIndex + 1} in ${round.label} needs at least two completed choices.` }
        }
        if (stage.type !== 'survey' && !isChoiceAnswerConfigured(question)) {
          return {
            roundIndex,
            stageIndex,
            questionIndex,
            message: getQuestionType(question) === 'multi-select'
              ? `Question ${questionIndex + 1} in ${round.label} needs one or more correct answers selected.`
              : `Question ${questionIndex + 1} in ${round.label} needs a correct answer selected.`,
          }
        }
      }
    }
  }
  return null
}

function handleSave() {
  const invalidQuestion = findFirstInvalidQuestion()
  if (invalidQuestion) {
    saveError.value = invalidQuestion.message
    activeRound.value = invalidQuestion.roundIndex
    activeStageIndex.value = invalidQuestion.stageIndex
    nextTick(() => {
      const card = scrollBody.value?.querySelector<HTMLElement>(`[data-question-index="${invalidQuestion.questionIndex}"]`)
      scrollCardIntoEditingView(card)
      const badge = card?.querySelector<HTMLButtonElement>('.qqm__badge')
      const firstInput = card?.querySelector<HTMLInputElement>('input[type="text"], textarea')
      ;(firstInput || badge)?.focus({ preventScroll: true })
    })
    return
  }
  saveError.value = ''
  saveSuccess.value = props.inline
    ? 'Questions saved into this draft. Next: review Map or save the template definition.'
    : ''
  draft.forEach((round) => {
    round.stages.forEach((stage, index) => {
      stage.order = index + 1
    })
  })
  emit('save', deepCloneRounds(draft))
}

function syncInlineDraft() {
  if (!props.inline) return
  emit('save', deepCloneRounds(draft))
}

function scrollCardIntoEditingView(card?: HTMLElement | null) {
  if (!card) return
  const performScroll = () => {
    if (props.inline && scrollBody.value) {
      const bodyRect = scrollBody.value.getBoundingClientRect()
      const cardRect = card.getBoundingClientRect()
      const offsetWithinBody = cardRect.top - bodyRect.top + scrollBody.value.scrollTop
      const targetTop = offsetWithinBody - 12
      scrollBody.value.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      })
      return
    }

    const quizSection = card.closest<HTMLElement>('#quiz-config')
    if (quizSection) {
      const sectionRect = quizSection.getBoundingClientRect()
      const cardRect = card.getBoundingClientRect()
      const offsetWithinSection = cardRect.top - sectionRect.top + quizSection.scrollTop
      const targetTop = offsetWithinSection - Math.max(28, quizSection.clientHeight * 0.08)
      quizSection.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      })
      return
    }

    const scrollParent = getScrollParent(card)
    if (!scrollParent || scrollParent === document.body || scrollParent === document.documentElement) {
      const rect = card.getBoundingClientRect()
      const targetTop = window.scrollY + rect.top - Math.max(120, window.innerHeight * 0.18)
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      })
      return
    }

    const parentRect = scrollParent.getBoundingClientRect()
    const cardRect = card.getBoundingClientRect()
    const offsetWithinParent = cardRect.top - parentRect.top + scrollParent.scrollTop
    const targetTop = offsetWithinParent - Math.max(120, scrollParent.clientHeight * 0.18)
    scrollParent.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    })
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(performScroll)
  })
  window.setTimeout(performScroll, 80)
  window.setTimeout(performScroll, 180)
}

function getScrollParent(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null
  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return document.scrollingElement as HTMLElement | null
}

function getRoundQuestionCount(round: QuizRound): number {
  return round.stages.reduce((sum, stage) => sum + stage.questions.length, 0)
}
</script>

<style scoped>
.qqm-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: calc(var(--base-layout-modal-z-index) + 200);
}

.qqm {
  --qqm-scale: 1;
  width: min(660px, 100%);
  max-height: min(92vh, 860px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qqm--inline {
  width: 100% !important;
  inline-size: 100% !important;
  min-width: 100%;
  max-height: none;
  justify-self: stretch;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.qqm--inline .qqm__description,
.qqm--inline .qqm__header h3,
.qqm--inline .qqm__tab,
.qqm--inline .qqm__tab--add,
.qqm--inline .qqm__tab-remove,
.qqm--inline .qqm__count,
.qqm--inline .qqm__empty,
.qqm--inline .qqm__field-label,
.qqm--inline .qqm__setting-label,
.qqm--inline .qqm__hint,
.qqm--inline .qqm__stage-count {
  color: rgba(237, 244, 255, 0.72);
}

.qqm--inline .qqm__tabs,
.qqm--inline .qqm__tab-list,
.qqm--inline .qqm__toolbar {
  border-color: rgba(122, 230, 177, 0.1);
}

.qqm--inline .qqm__round-settings,
.qqm--inline .qqm__stage-strip,
.qqm--inline .qqm__toolbar {
  background: rgba(255, 255, 255, 0.02);
}

.qqm--inline .qqm__tab:hover,
.qqm--inline .qqm__tab--add:hover,
.qqm--inline .qqm__tab-remove:hover,
.qqm--inline .qqm__stage-tab,
.qqm--inline .qqm__stage-tab-main,
.qqm--inline .qqm__q-number {
  color: #f5fbff;
}

.qqm--inline .qqm__tab--active {
  color: rgba(122, 230, 177, 0.96);
  border-bottom-color: rgba(122, 230, 177, 0.96);
}

.qqm--inline .qqm__tab-qcount {
  background: rgba(255, 255, 255, 0.08);
  color: #f5fbff;
}

.qqm--inline .qqm__tab--active .qqm__tab-qcount {
  background: rgba(122, 230, 177, 0.18);
}

.qqm--inline .qqm__stage-tab {
  border-color: rgba(122, 230, 177, 0.12);
  background: rgba(255, 255, 255, 0.04);
}

.qqm--inline .qqm__stage-tab--active {
  border-color: rgba(122, 230, 177, 0.38);
  background: rgba(122, 230, 177, 0.14);
}

.qqm--inline .qqm__setting-input,
.qqm--inline .qqm__field {
  color: #f5fbff;
  background: rgba(5, 13, 24, 0.4);
  border-color: rgba(122, 230, 177, 0.12);
}

.qqm--inline .qqm__field input,
.qqm--inline .qqm__field select,
.qqm--inline .qqm__choice-input {
  color: #f5fbff;
  background: rgba(5, 13, 24, 0.52);
  border-color: rgba(122, 230, 177, 0.12);
}

.qqm--inline .qqm__field input::placeholder,
.qqm--inline .qqm__choice-input::placeholder {
  color: rgba(237, 244, 255, 0.45);
}

.qqm--inline .qqm__card {
  border-color: rgba(122, 230, 177, 0.1);
  background: rgba(255, 255, 255, 0.04);
}

.qqm--inline .qqm__badge {
  border-color: rgba(122, 230, 177, 0.2);
  color: rgba(237, 244, 255, 0.72);
}

.qqm--inline .qqm__badge--correct {
  border-color: #39ff9c;
  background: rgba(57, 255, 156, 0.18);
  color: #d8fff0;
}

.qqm--inline .qqm__remove-btn {
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 99, 132, 0.22);
  background: rgba(255, 99, 132, 0.08);
  color: #ffd5df;
  font-size: 0.75rem;
  font-weight: 700;
}

.qqm--inline .qqm__remove-btn:hover {
  background: rgba(255, 99, 132, 0.16);
  color: #fff1f5;
}

.qqm--inline .qqm__add-choice-btn {
  border-color: rgba(122, 230, 177, 0.24);
  color: rgba(237, 244, 255, 0.72);
}

.qqm--inline .qqm__add-choice-btn:hover {
  border-color: rgba(122, 230, 177, 0.52);
  color: #f5fbff;
}

.qqm__header--inline {
  padding: 0.55rem 0.85rem 0.45rem;
}

.qqm__action--inline-save {
  width: 100%;
  min-width: 0;
  min-height: 1.7rem;
  padding: 0.24rem 0.6rem;
  font-size: 0.76rem;
}

.qqm__header {
  flex-shrink: 0;
}

.qqm__header-copy {
  display: grid;
  gap: 0.35rem;
  max-width: min(100%, 28rem);
  text-align: left;
  justify-items: start;
  align-self: flex-start;
}

.qqm--inline .qqm__header-copy {
  max-width: none;
}

.qqm__header-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  margin-left: auto;
  width: 9.5rem;
  min-height: 5.55rem;
  padding: 0.5rem 0.55rem;
  border: 1px solid rgba(122, 230, 177, 0.12);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.04);
  box-sizing: border-box;
}

.qqm__step-actions-label {
  width: 100%;
  color: #f5fbff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.qqm__step-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 1.65rem;
  padding: 0.22rem 0.55rem;
  border: 1px solid rgba(122, 230, 177, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(237, 244, 255, 0.86);
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.qqm__step-status--saved {
  border-color: rgba(122, 230, 177, 0.3);
  background: rgba(122, 230, 177, 0.12);
  color: rgba(122, 230, 177, 0.96);
}

.qqm__step-status--error {
  border-color: rgba(255, 152, 173, 0.28);
  background: rgba(255, 152, 173, 0.12);
  color: #ff98ad;
}

.qqm__header h3 {
  margin-bottom: 0;
}

.qqm__description {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.78rem;
  line-height: 1.28;
}

.qqm--inline .qqm__action--primary {
  border-color: rgba(122, 230, 177, 0.96);
  background: rgba(122, 230, 177, 0.96);
  color: #06111b;
}

.qqm--inline .qqm__action--primary:hover {
  border-color: rgba(122, 230, 177, 0.82);
  background: rgba(122, 230, 177, 0.82);
}

@media (max-width: 900px) {
  .qqm__header--inline {
    align-items: flex-start;
  }

  .qqm__header-actions {
    margin-left: 0;
    width: 100%;
  }
}

/* Round tabs */
.qqm__tabs {
  flex-shrink: 0;
  border-bottom: 1px solid var(--ui-inverse-border);
}

.qqm__tab-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.25rem;
}

.qqm__tab-list--select {
  display: none !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 0.4rem;
  padding: 0.55rem 1.25rem;
}

.qqm__round-select {
  width: auto;
  min-width: 12rem;
  padding: 0.4rem 0.65rem;
  border-radius: 999px;
  border: 1px solid var(--ui-inverse-border, rgba(122, 230, 177, 0.16));
  background: rgba(255, 255, 255, 0.06);
  color: var(--ui-inverse-text, #f5fbff);
  font-size: 0.82rem;
  font-weight: 600;
  appearance: auto;
  cursor: pointer;
  text-align: center;
}

.qqm__round-add-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: auto !important;
  height: auto !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0.25rem 0.7rem !important;
  border-radius: 999px !important;
  border: 1px dashed rgba(122, 230, 177, 0.22) !important;
  background: rgba(255, 255, 255, 0.04) !important;
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.72)) !important;
  font-size: 0.75rem !important;
  font-style: italic;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
}

.qqm__round-add-btn:hover {
  color: var(--ui-inverse-cta-bg, #37c88d) !important;
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.08) !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__tab {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.3rem;
  width: auto !important;
  height: auto !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0.32rem 0.7rem !important;
  border-radius: 999px !important;
  border: 1px solid rgba(122, 230, 177, 0.16) !important;
  background: rgba(255, 255, 255, 0.04) !important;
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.72)) !important;
  font-size: 0.78rem !important;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: color 150ms ease, border-color 150ms ease, background 150ms ease;
  line-height: 1.3;
  box-shadow: none !important;
  overflow: visible !important;
  position: relative;
}

.qqm__tab:hover {
  color: var(--ui-inverse-text, #f5fbff) !important;
  border-color: rgba(122, 230, 177, 0.28) !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__tab--active {
  color: var(--ui-inverse-text, #f5fbff) !important;
  border-color: var(--ui-inverse-cta-bg, #37c88d) !important;
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.14) !important;
}

.qqm__tab--add {
  font-style: italic;
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.72)) !important;
  border-style: dashed !important;
}

.qqm__tab--add:hover {
  color: var(--ui-inverse-cta-bg, #37c88d) !important;
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.08) !important;
}

.qqm__tab-label {
  max-width: 7rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qqm__tab-qcount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  background: rgba(16, 26, 52, 0.2);
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0 0.2rem;
}

.qqm__tab--active .qqm__tab-qcount {
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.15);
}

.qqm__tab-remove {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 1rem !important;
  height: 1rem !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 999px !important;
  border: none !important;
  background: rgba(255, 99, 132, 0.12) !important;
  color: rgba(255, 99, 132, 0.8) !important;
  font-size: 0.6rem !important;
  cursor: pointer;
  line-height: 1;
  transition: background 120ms ease, color 120ms ease;
  box-shadow: none !important;
}

.qqm__tab-remove:hover {
  background: rgba(255, 99, 132, 0.25) !important;
  color: #ff6384 !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__round-strip {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  min-width: 0;
}

.qqm__round-track {
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid var(--ui-inverse-border);
}

.qqm__round-track::-webkit-scrollbar {
  display: none;
}

.qqm__round-list {
  display: grid;
  align-items: stretch;
  gap: 0.4rem;
  min-width: 100%;
}

.qqm__round-actions {
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  padding-right: 0.75rem;
  border-bottom: 1px solid var(--ui-inverse-border);
  background: transparent;
}

.qqm__round-pill {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.35rem;
  justify-content: center !important;
  width: 100%;
  padding: 0.34rem 0.6rem !important;
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(122, 230, 177, 0.16) !important;
  color: rgba(237, 244, 255, 0.86) !important;
  font-size: 0.76rem !important;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: color 150ms ease, border-color 150ms ease, background 150ms ease;
  min-height: 0 !important;
  height: auto !important;
  margin: 0 !important;
  border-radius: 999px !important;
  box-shadow: inset 0 -2px 0 rgba(255, 255, 255, 0.06) !important;
  line-height: 1.1;
  box-sizing: border-box;
}

.qqm__round-pill:hover {
  color: #f5fbff;
  border-color: rgba(122, 230, 177, 0.28);
}

.qqm__round-pill--active {
  color: #f5fbff;
  border-color: var(--ui-inverse-cta-bg);
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.14);
  box-shadow: inset 0 -3px 0 var(--ui-inverse-cta-bg);
}

.qqm__round-pill--add {
  color: rgba(237, 244, 255, 0.88);
  opacity: 1;
  font-style: italic;
  width: auto;
  align-self: center;
  min-height: 36px;
  height: 36px;
  line-height: 36px;
  padding-top: 0;
  padding-bottom: 0;
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(122, 230, 177, 0.22);
  box-shadow: inset 0 -2px 0 rgba(255, 255, 255, 0.06);
}

.qqm__round-pill--add:hover {
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.08);
  color: var(--ui-inverse-cta-bg);
}

.qqm__round-pill-label {
  max-width: 6.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qqm__round-pill-qcount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.2rem;
  height: 1.2rem;
  border-radius: 999px;
  background: rgba(16, 26, 52, 0.2);
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0 0.25rem;
}

.qqm__round-pill--active .qqm__round-pill-qcount {
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.15);
}

.qqm__round-pill-remove {
  background: transparent;
  border: none;
  color: rgba(237, 244, 255, 0.62);
  cursor: pointer;
  font-size: 0.65rem;
  padding: 0.1rem;
  line-height: 1;
  opacity: 0.6;
  transition: opacity 150ms ease, color 150ms ease;
  margin: 0;
  min-height: unset;
  height: auto;
  width: auto;
  box-shadow: none;
  border-radius: 50%;
}

.qqm__round-pill-remove:hover {
  opacity: 1;
  color: #8a1027;
}

/* Round settings bar */
.qqm__round-settings {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  padding: 0.4rem 0.75rem;
  background: rgba(230, 255, 244, 0.06);
  flex-wrap: wrap;
}

.qqm__stage-strip {
  padding: 0 0.75rem 0.45rem;
  background: rgba(230, 255, 244, 0.03);
}

.qqm__stage-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  min-width: 0;
}

.qqm__stage-track {
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.qqm__stage-track::-webkit-scrollbar {
  display: none;
}

.qqm__stage-actions {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  align-self: flex-start;
  padding-top: 0;
  margin-top: 0;
}

.qqm__stage-tabs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: max-content;
}

.qqm__stage-item {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
}

.qqm__stage-tab {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.32rem;
  border: 1px dashed rgba(122, 230, 177, 0.24) !important;
  background: rgba(255, 255, 255, 0.03) !important;
  color: rgba(237, 244, 255, 0.78) !important;
  border-radius: 999px !important;
  padding: 0.22rem 0.5rem !important;
  font-size: 0.72rem !important;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1.1;
  min-height: 1.9rem !important;
  height: 1.9rem !important;
  width: auto !important;
  margin: 0 !important;
  box-sizing: border-box;
  box-shadow: none !important;
  overflow: visible !important;
}

.qqm__stage-tab-main {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  min-height: 1.9rem !important;
  height: 1.9rem !important;
  width: auto !important;
  box-sizing: border-box;
  line-height: 1.1;
  font-size: 0.72rem !important;
  font-weight: 600;
  color: rgba(237, 244, 255, 0.78) !important;
  box-shadow: none !important;
  min-width: unset;
  appearance: none;
  gap: 0.45rem;
  transform: translateY(1px);
  overflow: visible !important;
}

.qqm__stage-tab--active {
  border-color: var(--ui-inverse-cta-bg);
  background: rgba(var(--ui-inverse-cta-bg-rgb, 55, 200, 141), 0.14);
  color: #f5fbff;
}

.qqm__stage-tab--add {
  border-style: dashed !important;
  justify-content: center;
  transform: none !important;
  box-shadow: none !important;
  align-self: flex-start;
  margin: 0 !important;
}

.qqm__stage-tab--add:hover {
  transform: none !important;
  box-shadow: none !important;
}

.qqm__stage-remove {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 1.1rem !important;
  height: 1.1rem !important;
  min-height: 0 !important;
  border-radius: 999px !important;
  border: 1px solid rgba(255, 99, 132, 0.2) !important;
  background: rgba(255, 99, 132, 0.08) !important;
  color: #ffd5df !important;
  margin: 0 0 0 0.1rem !important;
  padding: 0 !important;
  line-height: 1;
  font-size: 0.52rem !important;
  box-shadow: none !important;
}

.qqm__stage-remove:hover {
  background: rgba(255, 99, 132, 0.16) !important;
  color: #fff1f5 !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__stage-count {
  color: var(--ui-inverse-text-muted);
  font-size: 0.66rem;
  line-height: 1;
}

.qqm__setting-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.qqm__setting-field--narrow {
  max-width: 7rem;
}

.qqm__setting-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--ui-inverse-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.qqm__setting-input,
.qqm__setting-select {
  height: 2.1rem;
  border-radius: 0.6rem;
  padding: 0 0.65rem;
  font-size: 0.82rem;
  color: var(--ui-inverse-text);
  background: rgba(16, 26, 52, 0.08);
  border: 1px solid rgba(16, 26, 52, 0.18);
  min-height: unset;
  width: auto;
  max-width: 100%;
}

.qqm__setting-input:disabled,
.qqm__setting-select:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* A4: Prompt Format / Response Type selects — match the General Knowledge
   theme dropdown on the Experience Source screen (pastel green background,
   dark navy text). Only restyles native <select> renderings of qqm__setting-input. */
select.qqm__setting-input {
  background: var(--vt-c-accent, #c8f7c5);
  color: #0a1628;
  border-color: var(--vt-c-accent, #c8f7c5);
  font-weight: 600;
}

select.qqm__setting-input:focus-visible {
  outline: 2px solid var(--vt-c-accent, #39ff14);
  outline-offset: 1px;
}

select.qqm__setting-input option {
  color: #0a1628;
}

/* Toolbar between settings and body */
.qqm__toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.42rem 0.75rem;
  border-bottom: 1px solid var(--ui-inverse-border);
  background: rgba(230, 255, 244, 0.12);
}

.qqm__count {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ui-inverse-text-muted);
  font-variant-numeric: tabular-nums;
}

.qqm__save-error {
  margin: 0.8rem 1.25rem 0;
  padding: 0.8rem 0.95rem;
  border-radius: 12px;
  background: rgba(255, 99, 132, 0.12);
  border: 1px solid rgba(255, 99, 132, 0.25);
  color: #ffd5df;
  font-size: 0.85rem;
}

.qqm__save-success {
  margin: 0.8rem 1.25rem 0;
  padding: 0.8rem 0.95rem;
  border-radius: 12px;
  background: rgba(57, 255, 156, 0.12);
  border: 1px solid rgba(57, 255, 156, 0.24);
  color: #d8fff0;
  font-size: 0.85rem;
}

.qqm__add-btn {
  font-size: 0.82rem !important;
  font-weight: 700;
  color: var(--ui-inverse-cta-text) !important;
  background: var(--ui-inverse-cta-bg) !important;
  border: 1px solid var(--ui-inverse-cta-bg) !important;
  border-radius: 999px !important;
  padding: 0.4rem 0.9rem !important;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  margin: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  width: auto !important;
  box-shadow: none !important;
}

.qqm__add-btn:hover:not(:disabled) {
  background: var(--ui-inverse-cta-bg-hover) !important;
  border-color: var(--ui-inverse-cta-bg-hover) !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__add-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Scrollable body */
.qqm__scroll-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.42rem 0.75rem 0.8rem;
}

.qqm__scroll-body--inline {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: 1.5rem;
}

/* A7: cap question summary list to ~4 rows visible, then scroll. Modal
   height stays fixed regardless of question count. Each row is ~2.7rem
   (padding 0.6 * 2 + content ~1.3rem) plus 0.4rem margin → ~3.1rem. Reserve
   ~12.8rem (4 rows + a sliver) plus padding. */
.qqm__scroll-body--questions {
  flex: 0 1 auto;
  max-height: calc(4 * 3.1rem + 1.4rem);
  overflow-y: auto;
}

.qqm__empty {
  text-align: center;
  color: var(--ui-inverse-text-muted);
  font-size: 0.9rem;
  padding: 2rem 0;
}

/* Question card */
.qqm__card {
  border: 1px solid var(--ui-inverse-border);
  border-radius: 0.85rem;
  padding: 0.85rem;
  margin-bottom: 0.65rem;
  background: rgba(230, 255, 244, 0.15);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.qqm--inline .qqm__card {
  padding: 0.65rem;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}

.qqm__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qqm__q-number {
  font-size: 0.78rem;
  font-weight: 800;
  color: var(--ui-inverse-cta-bg);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.qqm__remove-btn {
  background: transparent !important;
  border: none !important;
  color: var(--ui-inverse-text-muted) !important;
  cursor: pointer;
  font-size: 0.85rem !important;
  padding: 0.25rem !important;
  line-height: 1;
  transition: color 150ms ease;
  flex-shrink: 0;
  margin: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  width: auto !important;
  box-shadow: none !important;
}

.qqm__remove-btn:hover {
  color: #8a1027 !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__remove-btn--small {
  font-size: 0.7rem;
}

/* Field styling inside cards */
.qqm__field {
  display: grid;
  gap: 0.4rem;
  color: var(--ui-inverse-text);
  padding: 0.5rem 0.7rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 26, 52, 0.12);
  background: rgba(16, 26, 52, 0.06);
}

.qqm--inline .qqm__field {
  gap: 0.25rem;
  padding: 0.38rem 0.55rem;
}

.qqm__field--wide {
  width: 100%;
}

.qqm__field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.qqm__advanced {
  display: grid;
  gap: 0.15rem;
}

.qqm__advanced-summary {
  cursor: pointer;
  color: rgba(237, 244, 255, 0.72);
  font-size: 0.74rem;
  font-weight: 700;
  list-style: none;
  padding: 0;
  margin: 0;
}

.qqm__advanced-summary::-webkit-details-marker {
  display: none;
}

.qqm__advanced-body {
  display: grid;
  gap: 0.12rem;
}

.qqm__field-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--ui-inverse-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.qqm__field input,
.qqm__field select {
  width: 100%;
  min-height: 2.6rem;
  border-radius: 0.95rem;
  padding: 0.55rem 0.9rem;
}

/* Choices list */
.qqm__choices {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.qqm--inline .qqm__choices {
  gap: 0.28rem;
}

.qqm__choice-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.qqm--inline .qqm__choice-row {
  gap: 0.35rem;
}

/* A6: drag-to-reorder choices */
.qqm__choice-row--draggable {
  cursor: grab;
}

.qqm__choice-row--draggable:active {
  cursor: grabbing;
}

.qqm__choice-row--dragging {
  opacity: 0.45;
}

.qqm__choice-handle {
  flex-shrink: 0;
  width: 1rem;
  font-size: 0.75rem;
  letter-spacing: -0.15em;
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.4));
  user-select: none;
  cursor: grab;
}

.qqm__badge {
  width: 30px !important;
  min-width: 30px !important;
  height: 30px !important;
  min-height: 0 !important;
  border-radius: 50% !important;
  border: 2px solid var(--ui-inverse-border-strong) !important;
  background: transparent !important;
  color: var(--ui-inverse-text-muted) !important;
  font-size: 0.75rem !important;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
}

.qqm__badge--correct {
  border-color: #15803d !important;
  background: rgba(21, 128, 61, 0.18) !important;
  color: #15803d !important;
}

.qqm__choice-input {
  flex: 1 1 15rem;
  min-height: 2.4rem;
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
}

.qqm__add-choice-btn {
  font-size: 0.78rem !important;
  color: var(--ui-inverse-text-muted) !important;
  background: transparent !important;
  border: 1px dashed var(--ui-inverse-border-strong) !important;
  border-radius: 999px !important;
  padding: 0.4rem !important;
  cursor: pointer;
  width: 100%;
  transition: border-color 150ms ease, color 150ms ease;
  margin: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  box-shadow: none !important;
}

.qqm__add-choice-btn:hover {
  border-color: var(--ui-inverse-cta-bg) !important;
  color: var(--ui-inverse-cta-bg) !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__hint {
  font-size: 0.75rem;
  color: var(--ui-inverse-text-muted);
  margin: 0;
  padding-left: 0.25rem;
  opacity: 0.7;
}

/* Question summary rows */
.qqm__q-row {
  display: flex !important;
  align-items: center !important;
  gap: 0.55rem;
  width: 100% !important;
  height: auto !important;
  min-height: 0 !important;
  margin: 0 0 0.4rem !important;
  padding: 0.6rem 0.75rem !important;
  border-radius: 0.75rem !important;
  border: 1px solid var(--ui-inverse-border, rgba(122, 230, 177, 0.12)) !important;
  background: rgba(255, 255, 255, 0.03) !important;
  color: var(--ui-inverse-text, #f5fbff) !important;
  cursor: pointer;
  text-align: left !important;
  transition: border-color 150ms ease, background 150ms ease;
  box-shadow: none !important;
}

.qqm__q-row:hover {
  border-color: rgba(122, 230, 177, 0.28) !important;
  background: rgba(255, 255, 255, 0.06) !important;
  transform: none !important;
  box-shadow: none !important;
}

.qqm__q-row-number {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--ui-inverse-cta-bg, #37c88d);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.qqm__q-row-text {
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qqm__q-row-meta {
  flex-shrink: 0;
  font-size: 0.7rem;
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.55));
}

.qqm__q-row-arrow {
  flex-shrink: 0;
  font-size: 1.2rem;
  font-weight: 300;
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.4));
  line-height: 1;
}

/* A5: per-question Remove on each row in modal mode. Wraps the existing
   click-to-edit area in a button + adds a small ✕ remove button at the end. */
.qqm__q-row--with-remove {
  cursor: default;
  padding: 0 !important;
  gap: 0 !important;
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid var(--ui-inverse-border, rgba(122, 230, 177, 0.12)) !important;
  overflow: hidden;
}

.qqm__q-row-main {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.6rem 0.75rem;
  background: transparent;
  border: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
  min-width: 0;
  font: inherit;
}

.qqm__q-row-main:hover {
  background: rgba(255, 255, 255, 0.04);
}

.qqm__q-row-remove {
  flex-shrink: 0;
  width: 2.1rem;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-left: 1px solid var(--ui-inverse-border, rgba(122, 230, 177, 0.12));
  color: var(--ui-inverse-text-muted, rgba(237, 244, 255, 0.55));
  cursor: pointer;
  font-size: 0.95rem;
  transition: background 150ms ease, color 150ms ease;
}

.qqm__q-row-remove:hover {
  background: rgba(255, 49, 49, 0.18);
  color: #ff3131;
}

/* Nested question editor modal */
.qqm-overlay--nested {
  z-index: calc(var(--base-layout-modal-z-index) + 300) !important;
  background: var(--ui-inverse-overlay, rgba(6, 17, 27, 0.75)) !important;
}

.qqm--nested {
  width: min(580px, 100%) !important;
  max-height: min(88vh, 780px);
  background: linear-gradient(165deg, var(--ui-inverse-surface, #1a2e3e) 0%, var(--ui-inverse-surface-soft, #152232) 100%) !important;
  border: 1px solid var(--ui-inverse-border-strong, rgba(122, 230, 177, 0.18)) !important;
  border-radius: 1.25rem !important;
}

.qqm--nested .qqm__scroll-body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.qqm--add-round {
  width: min(400px, 92%) !important;
  max-height: none;
}

/* Footer */
.qqm__footer {
  flex-shrink: 0;
}

.qqm__footer--inline {
  padding: 0 1rem 1rem;
}

.qqm__actions--inline {
  justify-content: flex-start;
}

.qqm__actions--inline > button {
  flex: 0 0 auto;
  min-width: 10rem;
}

.qqm__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  width: 100%;
}

.qqm__actions > button {
  flex: 1 1 0;
}

.qqm__action {
  min-height: 2.85rem;
  margin: 0;
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-family: var(--ui-inverse-font-family);
  font-weight: 700;
  transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}

.qqm__action--secondary {
  border: 1px solid var(--ui-inverse-border-strong);
  background: transparent;
  color: var(--ui-inverse-text);
}

.qqm__action--secondary:hover {
  background: rgba(16, 26, 52, 0.08);
  border-color: rgba(16, 26, 52, 0.36);
}

.qqm__action--primary {
  border: 1px solid var(--ui-inverse-cta-bg);
  background: var(--ui-inverse-cta-bg);
  color: var(--ui-inverse-cta-text);
}

.qqm__action--primary:hover {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.qqm__header-actions .qqm__action--inline-save {
  min-height: 1.7rem !important;
  padding: 0.24rem 0.6rem !important;
  font-size: 0.76rem !important;
  text-align: center !important;
}

/* Desktop scaling */
@media (min-width: 769px) {
  .qqm {
    --qqm-scale: calc(var(--desktop-modal-scale, 1) * var(--desktop-display-density-scale, 1));
    width: min(calc(660px * var(--qqm-scale)), 100%);
    max-height: min(92vh, calc(860px * var(--qqm-scale)));
  }

  .qqm__header {
    padding:
      max(1.1rem, calc(1.5rem * var(--qqm-scale)))
      max(2.2rem, calc(3.5rem * var(--qqm-scale)));
  }

  .qqm__description {
    font-size: calc(0.92rem * var(--qqm-scale));
  }

  .qqm__tab-list {
    padding: 0 max(1rem, calc(1.25rem * var(--qqm-scale)));
  }

  .qqm__round-settings {
    padding:
      max(0.55rem, calc(0.65rem * var(--qqm-scale)))
      max(1rem, calc(1.25rem * var(--qqm-scale)));
  }

  .qqm__toolbar {
    padding:
      max(0.5rem, calc(0.6rem * var(--qqm-scale)))
      max(1rem, calc(1.25rem * var(--qqm-scale)));
  }

  .qqm__count {
    font-size: calc(0.82rem * var(--qqm-scale));
  }

  .qqm__add-btn {
    font-size: calc(0.82rem * var(--qqm-scale));
    padding: calc(0.4rem * var(--qqm-scale)) calc(0.9rem * var(--qqm-scale));
  }

  .qqm__scroll-body {
    padding:
      max(0.5rem, calc(0.65rem * var(--qqm-scale)))
      max(1rem, calc(1.25rem * var(--qqm-scale)))
      max(0.8rem, calc(1rem * var(--qqm-scale)));
  }

  .qqm__card {
    padding: max(0.72rem, calc(0.85rem * var(--qqm-scale)));
    margin-bottom: max(0.55rem, calc(0.65rem * var(--qqm-scale)));
    border-radius: max(0.72rem, calc(0.85rem * var(--qqm-scale)));
    gap: max(0.5rem, calc(0.6rem * var(--qqm-scale)));
  }

  .qqm__q-number {
    font-size: calc(0.78rem * var(--qqm-scale));
  }

  .qqm__field {
    gap: max(0.32rem, calc(0.4rem * var(--qqm-scale)));
    padding: max(0.42rem, calc(0.5rem * var(--qqm-scale))) max(0.58rem, calc(0.7rem * var(--qqm-scale)));
    border-radius: max(0.65rem, calc(0.75rem * var(--qqm-scale)));
  }

  .qqm__field-label {
    font-size: calc(0.75rem * var(--qqm-scale));
  }

  .qqm__field input {
    min-height: calc(2.6rem * var(--qqm-scale));
    padding: calc(0.55rem * var(--qqm-scale)) calc(0.9rem * var(--qqm-scale));
  }

  .qqm__badge {
    width: calc(30px * var(--qqm-scale));
    min-width: calc(30px * var(--qqm-scale));
    height: calc(30px * var(--qqm-scale));
    font-size: calc(0.75rem * var(--qqm-scale));
  }

  .qqm__choice-input {
    min-height: calc(2.4rem * var(--qqm-scale));
    padding: calc(0.45rem * var(--qqm-scale)) calc(0.8rem * var(--qqm-scale));
  }

  .qqm__action {
    min-height: calc(2.85rem * var(--qqm-scale));
    padding: calc(0.65rem * var(--qqm-scale)) calc(1rem * var(--qqm-scale));
    font-size: calc(0.94rem * var(--qqm-scale));
  }
}

@media (min-width: 769px) and (max-height: 900px) {
  .qqm-overlay {
    align-items: flex-start;
    padding: 1.25rem 1rem;
  }
}

@media (max-width: 640px) {
  .qqm {
    max-height: min(94vh, 860px);
  }

  .qqm__header,
  .qqm__scroll-body,
  .qqm__toolbar,
  .qqm__actions,
  .qqm__tab-list {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .qqm__round-settings {
    padding-left: 1rem;
    padding-right: 1rem;
    gap: 0.5rem;
  }

  .qqm__tab-list--pills {
    display: none !important;
  }

  .qqm__tab-list--select {
    display: flex !important;
  }
}
</style>
